import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import apiClient from "../../api/apiClient";
import { hasModuleAccess } from "../../accessControl";
import ModalA11yWrapper from "../../ui/ModalA11yWrapper";
import Button from "../../ui/Button";

const types = { risks: "Risks", controls: "Controls", actions: "Actions" };
const pageSize = 20;

export function normalizeLinks(linked = {}) {
  return Object.fromEntries(Object.keys(types).map((type) => {
    const records = new Map();
    (Array.isArray(linked?.[type]) ? linked[type] : []).forEach((value) => {
      const id = Number(value?.id ?? value);
      if (Number.isInteger(id) && id > 0) records.set(id, {
        type: type.slice(0, -1), id, title: value?.title || value?.name || `${types[type]} #${id}`,
      });
    });
    return [type, [...records.values()]];
  }));
}

export function BoardPackItemLinkModal({ linked, allowedTypes, onSave, onClose }) {
  const [selection, setSelection] = useState(() => normalizeLinks(linked));
  const [type, setType] = useState(allowedTypes[0]);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [retry, setRetry] = useState(0);
  const [page, setPage] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const searchRef = useRef(null);
  const savingRef = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setReadError("");
    const timer = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/${type}/`, { params: { q: search, limit: pageSize, offset } });
        if (!Array.isArray(data?.items) || !Number.isInteger(data?.total) || data.total < 0 || data.items.some((item) => !Number.isInteger(item?.id) || item.id <= 0 || !(item.title || item.name))) {
          throw new Error("Invalid record response");
        }
        if (active) setPage(data);
      } catch (_error) {
        if (active) setReadError("Unable to load records. Please try again.");
      } finally {
        if (active) setLoading(false);
      }
    }, 200);
    return () => { active = false; clearTimeout(timer); };
  }, [type, search, offset, retry]);

  const toggle = (record, checked) => setSelection((previous) => ({
    ...previous,
    [type]: checked
      ? normalizeLinks({ [type]: [...previous[type], record] })[type]
      : previous[type].filter((item) => item.id !== record.id),
  }));
  const close = () => { if (!savingRef.current) onClose(); };
  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError("");
    try {
      await onSave(Object.fromEntries(allowedTypes.map((key) => [key, selection[key]])));
      onClose();
    } catch (_error) {
      setSaveError("Unable to save links. Your selection is still here. Please try again.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return createPortal(
    <ModalA11yWrapper open onClose={close} initialFocusRef={searchRef} labelledBy="agenda-links-title"
      containerStyle={{ width: 640, maxWidth: "95vw", padding: 24 }}>
      <h2 id="agenda-links-title">Link agenda items</h2>
      <p>Select existing records. Uncheck a record or remove it from the selection to unlink it, then save.</p>
      <fieldset disabled={saving} style={{ border: 0, padding: 0 }}>
        <label>Record type <select value={type} onChange={(event) => { setType(event.target.value); setOffset(0); setSearch(""); }}>
          {allowedTypes.map((key) => <option key={key} value={key}>{types[key]}</option>)}
        </select></label>
        <label style={{ display: "block", margin: "16px 0" }}>Search records <input ref={searchRef} value={search}
          onChange={(event) => { setSearch(event.target.value); setOffset(0); }} /></label>
        {loading ? <p role="status">Loading records…</p> : readError ? <div role="alert">{readError} <Button onClick={() => setRetry((value) => value + 1)}>Retry</Button></div> : <>
          {!page.items.length && <p>No matching records.</p>}
          <ul style={{ listStyle: "none", padding: 0 }}>
            {page.items.map((record) => <li key={record.id} style={{ padding: "6px 0" }}>
              <label><input type="checkbox" checked={selection[type].some((item) => item.id === record.id)}
                onChange={(event) => toggle({ ...record, title: record.title || record.name }, event.target.checked)} /> {record.title || record.name} (#{record.id})</label>
            </li>)}
          </ul>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Button disabled={offset === 0} onClick={() => setOffset((value) => Math.max(0, value - pageSize))}>Previous</Button>
            <span>{page.total ? `${offset + 1}–${Math.min(offset + pageSize, page.total)} of ${page.total}` : "0 records"}</span>
            <Button disabled={offset + pageSize >= page.total} onClick={() => setOffset((value) => value + pageSize)}>Next</Button>
          </div>
        </>}
        <h3>Selected records</h3>
        {allowedTypes.map((key) => <div key={key}>
          <strong>{types[key]}: {selection[key].length}</strong>
          <ul>{selection[key].map((record) => <li key={record.id}>
            {record.title} (#{record.id}) <Button aria-label={`Remove ${record.title}`} onClick={() => setSelection((previous) => ({ ...previous, [key]: previous[key].filter((item) => item.id !== record.id) }))}>Remove</Button>
          </li>)}</ul>
        </div>)}
      </fieldset>
      {saveError && <p role="alert">{saveError}</p>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
        <Button disabled={saving} onClick={close}>Cancel</Button>
        <Button disabled={saving} onClick={save}>{saving ? "Saving…" : "Save links"}</Button>
      </div>
    </ModalA11yWrapper>, document.body
  );
}

export default function BoardPackItemLinks({ linked, user, editable, onSave }) {
  const [open, setOpen] = useState(false);
  const canEdit = editable && hasModuleAccess(user, "governance", "EDIT");
  const allowedTypes = Object.keys(types).filter((type) => hasModuleAccess(user, type, "VIEW"));
  const normalized = normalizeLinks(linked);
  return <div>
    <span style={{ fontSize: 12, color: "#6b7280" }}>Linked items</span>
    <div style={{ fontSize: 12 }}>{allowedTypes.map((key) => `${types[key]}: ${normalized[key].length}`).join(" - ")}</div>
    {allowedTypes.map((key) => <ul key={key} style={{ paddingLeft: 18, margin: "4px 0" }}>
      {normalized[key].map((record) => <li key={record.id}><a href={`/${key}/${record.id}`}>{types[key].slice(0, -1)} #{record.id}: {record.title}</a></li>)}
    </ul>)}
    {canEdit && allowedTypes.length > 0 && <Button onClick={() => setOpen(true)}>+ Link item</Button>}
    {open && canEdit && allowedTypes.length > 0 && <BoardPackItemLinkModal key={allowedTypes.join(",")} linked={normalized} allowedTypes={allowedTypes} onSave={onSave} onClose={() => setOpen(false)} />}
  </div>;
}
